import { PermissionUser } from "app-roles/models";
import React, { useState } from "react";
import { DirectLink, getAdminRoutes, NavCategory, RouteConfig } from "Routes";
import { useAppConfig } from "util/AppConfig";
import { makeStyles } from "makeStyles";
import { FormControlLabel, FormGroup, Switch } from "@mui/material";

const useStyles = makeStyles()((theme) => ({
  crossedOut: {
    textDecoration: "line-through",
    color: theme.palette.text.disabled
  }
}));

interface Props {
  user?: PermissionUser | null;
}

const UserScreensItem: React.FunctionComponent<{ user?: PermissionUser | null; item: RouteConfig | DirectLink; showCrossedOut?: boolean }> =
  ({ user, item, showCrossedOut }) => {
    const { classes, cx } = useStyles();

    const itemIsVisible =
      !item.authorizedPermissions ||
      item.authorizedPermissions.length === 0 ||
      item.authorizedPermissions.some((perm) => user?.appRoles?.some((role) => role.identifier === perm));

    if (!itemIsVisible && !showCrossedOut) {
      return null;
    }

    return (
      <li key={item.navLabel} className={cx({ [classes.crossedOut]: showCrossedOut && !itemIsVisible })}>
        {item.navLabel}
      </li>
    );
  };

const UserScreensCategory: React.FunctionComponent<{ user?: PermissionUser | null; category: NavCategory; showCrossedOut?: boolean }> = ({
  user,
  category,
  showCrossedOut
}) => {
  const { classes, cx } = useStyles();

  // check if any items are visible
  const anyVisibleItems = category.navItems?.some((item) => {
    if (!item.authorizedPermissions || item.authorizedPermissions.length === 0) {
      return true;
    }
    return item.authorizedPermissions.some((perm) => user?.appRoles?.some((role) => role.identifier === perm));
  });

  if (!anyVisibleItems && !showCrossedOut) {
    return null;
  }

  return (
    <div>
      <h3 className={cx({ [classes.crossedOut]: showCrossedOut && !anyVisibleItems })}>{category.categoryName}</h3>
      <ul>
        {category.navItems?.map((item) => (
          <UserScreensItem key={item.navLabel} user={user} item={item} showCrossedOut={showCrossedOut} />
        ))}
      </ul>
    </div>
  );
};

const UserScreens: React.FunctionComponent<Props> = ({ user }) => {
  const appConfig = useAppConfig();
  const adminRoutes = getAdminRoutes(appConfig);
  const [showCrossedOut, setShowCrossedOut] = useState(false);

  return (
    <div>
      <FormGroup>
        <FormControlLabel
          control={<Switch checked={showCrossedOut} onClick={() => setShowCrossedOut(!showCrossedOut)} />}
          label="Show all"
        />
      </FormGroup>
      {adminRoutes.map((item) => {
        const asCategory = item as NavCategory;
        const asRoute = item as RouteConfig;
        if (asCategory.categoryName) {
          return <UserScreensCategory user={user} category={asCategory} showCrossedOut={showCrossedOut} />;
        }
        return <UserScreensItem key={asRoute.path} user={user} item={asRoute} showCrossedOut={showCrossedOut} />;
      })}
    </div>
  );
};

export default UserScreens;
